import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import AdaptivePageShell from '../../features/adaptive-learning/AdaptivePageShell';

export default function WeeklySummaryPage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareMsg, setShareMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/v1/learning/weekly-summary');
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load();
  }, [load]);

  const share = async () => {
    if (!data?.shareable) return;
    const text = `My QnaRead week: ${data.shareable.streak}-day streak · Learning Score ${data.shareable.learningScore} · ${data.shareable.xp} XP`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My QnaRead week', text });
      } else {
        await navigator.clipboard.writeText(text);
        setShareMsg('Copied to clipboard — no private notebook titles included.');
      }
    } catch {
      setShareMsg('Share cancelled.');
    }
    setTimeout(() => setShareMsg(''), 4000);
  };

  return (
    <>
      <PageMeta title="Weekly summary" description="Your learning week on QnaRead." />
      <AdaptivePageShell userProfile={userProfile} loading={loading && !data}>
        <header className="adaptive-hero">
          <h1>Your week on QnaRead</h1>
          <p className="adaptive-hero-lead">{data?.disclaimer || 'Activity from your account only.'}</p>
        </header>

        {loading && !data && (
          <>
            <div className="adaptive-skeleton" />
            <div className="adaptive-skeleton" />
          </>
        )}

        {data && (
          <>
            <div className="weekly-grid">
              <div className="weekly-stat">
                Streak
                <strong>{data.streak ?? 0} days</strong>
              </div>
              <div className="weekly-stat">
                Learning Score
                <strong>{data.learningScore ?? '—'}</strong>
              </div>
              <div className="weekly-stat">
                Lifetime XP
                <strong>{data.lifetimeXp ?? 0}</strong>
              </div>
              <div className="weekly-stat">
                Saved / due
                <strong>
                  {data.savedCount ?? 0} saved · {data.dueCount ?? 0} due
                </strong>
              </div>
              <div className="weekly-stat weekly-focus">
                Next week
                <strong>{data.nextWeekFocus || 'Keep your daily plan going'}</strong>
                {data.weakestArea && (
                  <span className="adaptive-muted">Weakest signal: {data.weakestArea}</span>
                )}
              </div>
            </div>
            <div className="adaptive-start-row">
              <button type="button" className="learn-btn learn-btn--primary" onClick={share}>
                Share achievements
              </button>
              <Link to="/hub/today" className="learn-btn learn-btn-secondary">
                Today&apos;s plan
              </Link>
              <Link to="/mastery" className="learn-btn learn-btn-secondary">
                Knowledge map
              </Link>
            </div>
            {shareMsg && <p className="adaptive-muted" role="status">{shareMsg}</p>}
          </>
        )}
      </AdaptivePageShell>
    </>
  );
}
